import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "@/shared/icons";
import { cn } from "@/shared/utils/cn";

type Tone = "default" | "danger";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (typeof document === "undefined" || !open) return null;

  return createPortal(
    <>
      <div className="confirm-backdrop" onClick={loading ? undefined : onClose} aria-hidden />
      <div role="dialog" aria-modal="true" aria-label={title} className="confirm-shell" data-testid="confirm-dialog">
        <div className={cn("confirm-card", tone === "danger" && "is-danger")}>
          <div className="confirm-hd">
            <div className={cn("confirm-icon", tone === "danger" && "is-danger")}>
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1">
              <h2 className="confirm-title">{title}</h2>
              <div className="confirm-msg">{message}</div>
            </div>
            <button
              type="button"
              className="confirm-close"
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <div className="confirm-ft">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
              {cancelLabel}
            </button>
            <button
              type="button"
              data-testid="confirm-dialog-confirm"
              className={cn("btn", tone === "danger" ? "btn-danger" : "btn-primary")}
              onClick={() => void onConfirm()}
              disabled={loading}
            >
              {loading ? "Working..." : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

interface UseConfirmInput {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  tone?: Tone;
}

export function useConfirm() {
  const [state, setState] = useState<
    | (UseConfirmInput & { resolve: (v: boolean) => void; loading: boolean })
    | null
  >(null);

  const confirm = (input: UseConfirmInput) =>
    new Promise<boolean>((resolve) => {
      setState({ ...input, resolve, loading: false });
    });

  const close = (v: boolean) => {
    if (!state) return;
    state.resolve(v);
    setState(null);
  };

  const node = state ? (
    <ConfirmDialog
      open
      title={state.title}
      message={state.message}
      tone={state.tone}
      confirmLabel={state.confirmLabel}
      onConfirm={() => close(true)}
      onClose={() => close(false)}
    />
  ) : null;

  return { confirm, node };
}
